import { createFileRoute, Link } from "@tanstack/react-router";
import { canonical } from "@/lib/seo";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Travel Guides & Local Tips - Malak BnB Resort, Crystal Beach" },
      {
        name: "description",
        content:
          "Travel guides, local tips, and seasonal highlights from Malak BnB Resort in Crystal Beach, Ontario. Plan your Lake Erie getaway with insider knowledge.",
      },
      {
        property: "og:title",
        content: "Travel Guides & Local Tips - Malak BnB Resort, Crystal Beach",
      },
      {
        property: "og:description",
        content:
          "Guides, seasonal posts, and local tips from a Crystal Beach bed and breakfast steps from Bay Beach and Lake Erie.",
      },
      { property: "og:url", content: "https://malakbnbresort.ca/blog" },
      { property: "og:image", content: "/images/beach-bay.webp" },
    ],
    links: [canonical("/blog")],
  }),
  component: BlogPage,
});

const POSTS = [
  {
    title: "Things to Do Near Crystal Beach, Ontario",
    excerpt:
      "Bay Beach, cycling routes, Ridge Beach, the marina, and a day trip to Niagara Falls. A local's guide to Crystal Beach, written from a five-minute walk away.",
    image: "/images/explore-crystal-beach.webp",
    imageAlt: "Crystal Beach town and Lake Erie shoreline Ontario",
    tag: "Travel Guide",
    tagColor: "bg-teal-50 text-teal-700",
    href: "/blog/things-to-do-near-crystal-beach-ontario",
    featured: true,
  },
  {
    title: "Bed and Breakfast in Crystal Beach, Ontario",
    excerpt:
      "Everything you need to know about staying at Malak BnB Resort, a boutique family-owned bed and breakfast steps from Bay Beach and Lake Erie.",
    image: "/images/hero-exterior.webp",
    imageAlt: "Exterior of Malak BnB Resort in Crystal Beach Ontario",
    tag: "Location Guide",
    tagColor: "bg-blue-50 text-blue-700",
    href: "/crystal-beach-bed-and-breakfast",
    featured: true,
  },
  {
    title: "Summer Stays at Crystal Beach",
    excerpt:
      "Crystal Beach in summer: Bay Beach five minutes on foot, a heated outdoor pool, continental breakfast, and long afternoons with nowhere to be.",
    image: "/images/beach-bay.webp",
    imageAlt: "Bay Beach at Crystal Beach Ontario on Lake Erie in summer",
    tag: "Seasonal",
    tagColor: "bg-amber-50 text-amber-700",
    href: "/summer-stays-crystal-beach",
    featured: true,
  },
  {
    title: "Fort Erie Bed and Breakfast Near Lake Erie",
    excerpt:
      "Malak BnB Resort sits in Crystal Beach, a lakeside community inside the Town of Fort Erie, ten minutes from downtown and fifteen from the Peace Bridge.",
    image: "/images/hero-exterior.webp",
    imageAlt: "Exterior of Malak BnB Resort in Fort Erie's Crystal Beach community, Ontario",
    tag: "Location Guide",
    tagColor: "bg-blue-50 text-blue-700",
    href: "/fort-erie-bed-and-breakfast",
    featured: false,
  },
  {
    title: "How to Request a Reservation at Malak BnB Resort",
    excerpt:
      "Malak does not use automated booking systems. Every reservation request is reviewed personally before confirmation. Here is how the process works.",
    image: "/images/gallery-bedroom-warm.webp",
    imageAlt: "Warmly lit bedroom at Malak BnB Resort Crystal Beach Ontario",
    tag: "Booking Guide",
    tagColor: "bg-green-50 text-green-700",
    href: "/reservation-details",
    featured: false,
  },
  {
    title: "About Malak BnB Resort",
    excerpt:
      "Jay, Hiba, and Gina built Malak into something guests come back to. A family-owned boutique BnB in Crystal Beach, Ontario with a story worth knowing.",
    image: "/images/gallery-staircase-chandelier.webp",
    imageAlt: "Grand chandelier staircase inside Malak BnB Resort Crystal Beach",
    tag: "Our Story",
    tagColor: "bg-rose-50 text-rose-700",
    href: "/about",
    featured: false,
  },
  {
    title: "A Weekend Getaway to Crystal Beach and Lake Erie",
    excerpt:
      "A two night itinerary for Crystal Beach: Friday arrival, a full Saturday between the beach and the pool, and a slow Sunday before checkout.",
    image: "/images/beach-bay.webp",
    imageAlt: "Bay Beach at Crystal Beach, Ontario at golden hour",
    tag: "Travel Guide",
    tagColor: "bg-teal-50 text-teal-700",
    href: "/blog/weekend-getaway-crystal-beach-lake-erie",
    featured: false,
  },
  {
    title: "Cycling-Friendly Accommodation in Niagara",
    excerpt:
      "A quiet, flat-road base for cycling the Niagara Region, close to Cycle and Stay Niagara routes, with breakfast and a pool to recover in.",
    image: "/images/explore-biking.webp",
    imageAlt: "Cycling through the streets of Crystal Beach, Ontario",
    tag: "Location Guide",
    tagColor: "bg-blue-50 text-blue-700",
    href: "/cycling-friendly-accommodation-niagara",
    featured: false,
  },
  {
    title: "Exploring Crystal Beach by Bike",
    excerpt:
      "The town loop, the waterfront path, and rides out toward Ridgeway and Fort Erie, all flat enough for an easy afternoon.",
    image: "/images/autumn-cycling.webp",
    imageAlt: "Cyclist riding a tree-lined path in fall",
    tag: "Travel Guide",
    tagColor: "bg-teal-50 text-teal-700",
    href: "/blog/exploring-crystal-beach-by-bike",
    featured: false,
  },
  {
    title: "Visiting Niagara Falls While Staying in Crystal Beach",
    excerpt:
      "Niagara Falls is a 30 minute drive from Crystal Beach. How to plan the day trip, and why a quieter base beats staying at the falls.",
    image: "/images/niagara-falls.webp",
    imageAlt: "Niagara Falls viewed from the Canadian side",
    tag: "Travel Guide",
    tagColor: "bg-teal-50 text-teal-700",
    href: "/blog/niagara-falls-day-trip-from-crystal-beach",
    featured: false,
  },
  {
    title: "Places to Eat Near Crystal Beach",
    excerpt:
      "From casual pizza to a proper sit-down dinner, where to eat near Crystal Beach and Ridgeway, Ontario.",
    image: "/images/dining-out.webp",
    imageAlt: "A restaurant table set for dinner",
    tag: "Travel Guide",
    tagColor: "bg-teal-50 text-teal-700",
    href: "/blog/restaurants-near-crystal-beach",
    featured: false,
  },
  {
    title: "What to Know Before Your Stay at Malak BnB Resort",
    excerpt:
      "A first-time guest guide: booking, arrival, what's included, and a few local tips before you go.",
    image: "/images/gallery-bedroom-warm.webp",
    imageAlt: "Warmly lit bedroom at Malak BnB Resort in Crystal Beach",
    tag: "Booking Guide",
    tagColor: "bg-green-50 text-green-700",
    href: "/blog/what-to-know-before-your-stay",
    featured: false,
  },
  {
    title: "Fall Getaways in Crystal Beach and Niagara",
    excerpt:
      "Crystal Beach in fall: fewer crowds, cooler air, and a Niagara Falls day trip with shorter lines and turning leaves.",
    image: "/images/niagara-falls-autumn.webp",
    imageAlt: "Niagara Falls surrounded by autumn foliage",
    tag: "Seasonal",
    tagColor: "bg-amber-50 text-amber-700",
    href: "/blog/fall-getaway-crystal-beach-niagara",
    featured: false,
  },
  {
    title: "Planning a Beach Day at Bay Beach",
    excerpt:
      "When to go, what to bring, and how to make a full day of it at Bay Beach in Crystal Beach, Ontario.",
    image: "/images/beach-bay.webp",
    imageAlt: "Bay Beach at Crystal Beach, Ontario on Lake Erie",
    tag: "Travel Guide",
    tagColor: "bg-teal-50 text-teal-700",
    href: "/blog/bay-beach-crystal-beach-guide",
    featured: false,
  },
  {
    title: "Frequently Asked Questions",
    excerpt:
      "Location, rooms, booking, breakfast, the pool, and events. Answers to the questions guests ask most often about Malak BnB Resort.",
    image: "/images/hero-exterior.webp",
    imageAlt: "Exterior of Malak BnB Resort in Crystal Beach, Ontario",
    tag: "FAQ",
    tagColor: "bg-violet-50 text-violet-700",
    href: "/faq",
    featured: false,
  },
  {
    title: "Quiet Off-Season Stays Near Lake Erie",
    excerpt:
      "Crystal Beach slows down once summer ends. Fewer crowds, cozy rooms, and breakfast every morning at Malak BnB Resort.",
    image: "/images/cozy-lake-winter.webp",
    imageAlt: "Cozy living room with fireplace and a lake view",
    tag: "Seasonal",
    tagColor: "bg-amber-50 text-amber-700",
    href: "/blog/off-season-stays-lake-erie",
    featured: false,
  },
  {
    title: "Small Events and Private Gatherings in Crystal Beach",
    excerpt:
      "Birthdays, anniversaries, and small family gatherings for up to 25 guests, with poolside setups and in-house catering.",
    image: "/images/events-food.webp",
    imageAlt: "Table setup for a small private gathering at Malak BnB Resort",
    tag: "Events",
    tagColor: "bg-rose-50 text-rose-700",
    href: "/small-events-crystal-beach",
    featured: false,
  },
  {
    title: "Seasonal Room Rates at Malak BnB Resort",
    excerpt:
      "Straightforward from-pricing for every room, how seasonal rates work, and what's included in every rate.",
    image: "/images/room-comfort.webp",
    imageAlt: "Comfortable room at Malak BnB Resort",
    tag: "Rates",
    tagColor: "bg-violet-50 text-violet-700",
    href: "/seasonal-rates-malak-bnb-resort",
    featured: false,
  },
];

const COMING_SOON = [
  "Comfortable Guest Amenities Near Lake Erie",
  "Family Gatherings and Group Stays Near Lake Erie",
  "Outdoor Pool and Lounge at Malak BnB Resort",
];

function BlogPage() {
  const featured = POSTS.filter((p) => p.featured);
  const rest = POSTS.filter((p) => !p.featured);

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[45vh] min-h-[300px] overflow-hidden">
        <img
          src="/images/explore-crystal-beach.webp"
          alt="Crystal Beach town and Lake Erie"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/65 via-foreground/20 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-14 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <p className="text-white/70 font-semibold text-sm uppercase tracking-widest mb-3">
              Malak BnB Resort
            </p>
            <h1 className="text-4xl sm:text-5xl font-heading text-white leading-tight">
              Travel Guides and Local Tips
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-muted-foreground leading-relaxed">
            Location guides, seasonal highlights, and the honest details about staying in Crystal
            Beach, Ontario. Written from the property, for the people planning to visit.
          </p>
        </div>
      </section>

      {/* Featured posts */}
      <section className="pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-8">
            Featured Guides
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {featured.map((post) => (
              <Link
                key={post.href}
                to={post.href as any}
                className="group block rounded-2xl overflow-hidden bg-card shadow-sm hover:shadow-xl transition-all duration-300"
              >
                <div className="aspect-[16/9] overflow-hidden relative">
                  <img
                    src={post.image}
                    alt={post.imageAlt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <span
                    className={`absolute top-4 left-4 text-xs font-semibold px-3 py-1 rounded-full ${post.tagColor}`}
                  >
                    {post.tag}
                  </span>
                </div>
                <div className="p-7">
                  <h2 className="font-heading text-xl text-foreground group-hover:text-primary transition-colors leading-snug mb-3">
                    {post.title}
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">{post.excerpt}</p>
                  <p className="mt-5 text-sm font-semibold text-primary">Read more →</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* More posts */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-8">
            More from Malak
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {rest.map((post) => (
              <Link
                key={post.href}
                to={post.href as any}
                className="group flex gap-5 bg-background rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 p-5"
              >
                <div className="w-28 h-28 rounded-xl overflow-hidden shrink-0">
                  <img
                    src={post.image}
                    alt={post.imageAlt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-col justify-center min-w-0">
                  <span
                    className={`text-xs font-semibold px-2 py-0.5 rounded-full self-start mb-2 ${post.tagColor}`}
                  >
                    {post.tag}
                  </span>
                  <h2 className="font-heading text-base text-foreground group-hover:text-primary transition-colors leading-snug mb-1">
                    {post.title}
                  </h2>
                  <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                    {post.excerpt}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Coming soon */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div>
              <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-4">
                Coming Soon
              </p>
              <h2 className="text-3xl font-heading text-foreground mb-6">More Guides on the Way</h2>
              <p className="text-muted-foreground leading-relaxed mb-8">
                New guides are published each month covering local attractions, seasonal stays,
                cycling routes, and the best way to plan a trip around Crystal Beach and the Niagara
                Region.
              </p>
              <ul className="space-y-3">
                {COMING_SOON.map((title) => (
                  <li key={title} className="flex items-center gap-3 text-sm text-muted-foreground">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                    {title}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-card rounded-2xl p-8 border border-border/50">
              <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-4">
                Ready to Stay?
              </p>
              <h3 className="text-2xl font-heading text-foreground mb-4 leading-snug">
                Plan your Crystal Beach getaway at Malak BnB Resort.
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                Four rooms, continental breakfast, outdoor heated pool, five minutes from Bay Beach.
                Submit a direct reservation request and the team will personally confirm your stay.
              </p>
              <div className="flex flex-col gap-3">
                <Link
                  to="/book"
                  className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  Request to Book
                </Link>
                <Link
                  to="/rooms"
                  className="inline-flex items-center justify-center rounded-full border border-border px-8 py-3 text-sm font-semibold text-foreground hover:bg-muted transition-colors"
                >
                  View Rooms and Rates
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
